/**
 * Daggerheart SRD 2.0 (25 Aug 2026) System Reference Catalog
 *
 * Mechanical character creation (CC-1) data model:
 * - 13 classes with their two domains, base stats, features, items, subclasses
 * - 24 ancestries with their top (feature 1) and bottom (feature 2) features
 * - 15 communities with descriptions and community features
 * - 10 domains with descriptions and all 1st-level domain cards
 * - Tier 1 primary & secondary weapons and Tier 1 armor
 * - Standard rules: trait modifiers, starting Hope/Stress/Proficiency, mixed ancestry
 */

export const DAGGERHEART_SRD_VERSION = "2.0";
export const DAGGERHEART_SRD_DATE = "2026-08-25";

export const STARTING_HOPE = 2;
export const STARTING_STRESS = 6;
export const STARTING_PROFICIENCY = 1;
export const STANDARD_TRAIT_MODIFIERS = [+2, +1, +1, +0, +0, -1] as const;

export type TraitName = "Agility" | "Strength" | "Finesse" | "Instinct" | "Presence" | "Knowledge";

export type DomainName =
  | "Arcana"
  | "Blade"
  | "Bone"
  | "Codex"
  | "Dread"
  | "Grace"
  | "Midnight"
  | "Sage"
  | "Splendor"
  | "Valor";

export interface SrdDomain {
  name: DomainName;
  description: string;
}

export const DOMAINS: Record<DomainName, SrdDomain> = {
  Arcana: {
    name: "Arcana",
    description: "Innate and instinctual magic. Taps into raw, enigmatic forces of the realms to manipulate energy and elements.",
  },
  Blade: {
    name: "Blade",
    description: "Weapon mastery. Dedication to achieving inexorable power over death through martial prowess.",
  },
  Bone: {
    name: "Bone",
    description: "Tactics and the body. Uncanny control over physical abilities and predicting behaviors in combat.",
  },
  Codex: {
    name: "Codex",
    description: "Intensive magical study. Equations of power recorded in tomes, scrolls, and runes beyond common wisdom.",
  },
  Dread: {
    name: "Dread",
    description: "Nightmares and fear. Calling forth monstrosities, enfeebling foes, and channeling terrifying destructive magic.",
  },
  Grace: {
    name: "Grace",
    description: "Charisma and perception. Rapturous storytelling, charming spells, raw magnetism, and mastery over language.",
  },
  Midnight: {
    name: "Midnight",
    description: "Shadows and secrecy. Obscurity, deception, deft magic, and uncovering sequestered treasures.",
  },
  Sage: {
    name: "Sage",
    description: "The natural world. Unfettered power of the earth, blooming vitality, and ferocity of ravenous predators.",
  },
  Splendor: {
    name: "Splendor",
    description: "Life and healing. Magnificent ability to mend wounds, give life, and control death.",
  },
  Valor: {
    name: "Valor",
    description: "Protection and fortitude. Formidable strength channeled to raise shields and defend allies in battle.",
  },
};

export interface SrdDomainCardLevel1 {
  name: string;
  domain: DomainName;
  level: 1;
  type: "Ability" | "Spell" | "Grimoire";
  recallCost: number;
  text: string;
}

export const LEVEL_1_DOMAIN_CARDS: SrdDomainCardLevel1[] = [
  // Arcana
  {
    name: "Rune Ward",
    domain: "Arcana",
    level: 1,
    type: "Spell",
    recallCost: 0,
    text: "You have a deeply personal trinket that can be infused with protective magic and held as a ward by you or an ally. Describe what it is and why it's important to you. The ward's holder can spend a Hope to reduce incoming damage by 1d8. If the Ward Die result is 8, the ward's power ends after it reduces damage this turn. It can be recharged for free on your next rest.",
  },
  {
    name: "Unleash Chaos",
    domain: "Arcana",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "At the beginning of a session, place a number of tokens equal to your Spellcast trait on this card. Make a Spellcast Roll against a target within Far range and spend any number of tokens to channel raw energy from within yourself to unleash against them. On a success, roll a number of d10s equal to the tokens you spent and deal that much magic damage to the target. Mark a Stress to replenish this card with tokens (up to your Spellcast trait). At the end of each session, clear all unspent tokens.",
  },
  {
    name: "Wall Walk",
    domain: "Arcana",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Spend a Hope to allow a creature you can touch to climb on walls and ceilings as easily as walking on the ground. This lasts until the end of the scene or you cast Wall Walk again.",
  },
  // Blade
  {
    name: "Get Back Up",
    domain: "Blade",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "When you take Severe damage, you can mark a Stress to reduce the severity by one threshold.",
  },
  {
    name: "Not Good Enough",
    domain: "Blade",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "When you roll your damage dice, you can reroll any 1s or 2s.",
  },
  {
    name: "Whirlwind",
    domain: "Blade",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "When you make a successful attack against a target within Very Close range, you can spend a Hope to use the attack against all other targets within Very Close range. All additional adversaries you succeed against with this ability take half damage.",
  },
  // Bone
  {
    name: "Deft Maneuvers",
    domain: "Bone",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "Once per rest, mark a Stress to sprint anywhere within Far range without making an Agility Roll to get there. If you end this movement within Melee range of an adversary and immediately make an attack against them, gain a +1 bonus to the attack roll.",
  },
  {
    name: "I See It Coming",
    domain: "Bone",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "When you're targeted by an attack made from beyond Melee range, you can mark a Stress to roll a d4 and gain a bonus to your Evasion equal to the result against the attack.",
  },
  {
    name: "Untouchable",
    domain: "Bone",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "Gain a bonus to your Evasion equal to half your Agility.",
  },
  // Codex
  {
    name: "Book of Ava",
    domain: "Codex",
    level: 1,
    type: "Grimoire",
    recallCost: 2,
    text: "Power Push: Make a Spellcast Roll against a target within Melee range. On a success, they're knocked back to Far range and take d10+2 magic damage using your Proficiency.\nTava's Armor: Spend a Hope to give a target you can touch a +1 bonus to their Armor Score until their next rest or you cast Tava's Armor again.\nIce Spike: Make a Spellcast Roll (12) to summon a large ice spike within Far range. If you use it as a weapon, make the Spellcast Roll against the target's Difficulty instead. On a success, deal d6 physical damage using your Proficiency.",
  },
  {
    name: "Book of Illiat",
    domain: "Codex",
    level: 1,
    type: "Grimoire",
    recallCost: 2,
    text: "Slumber: Make a Spellcast Roll against a target within Very Close range. On a success, they're Asleep until they take damage or the GM spends a Fear on their turn to clear this condition.\nArcane Barrage: Once per rest, spend any number of Hope and shoot magical projectiles that strike a target of your choice within Close range. Roll a number of d6s equal to the Hope spent and deal that much magic damage to the target.\nTelepathy: Spend a Hope to open a line of mental communication with one target you can see. This connection lasts until your next rest or you cast Telepathy again.",
  },
  {
    name: "Book of Tyfar",
    domain: "Codex",
    level: 1,
    type: "Grimoire",
    recallCost: 2,
    text: "Wild Flame: Make a Spellcast Roll against up to three adversaries within Melee range. Targets you succeed against take 2d6 magic damage and must mark a Stress as flames erupt from your hand.\nMagic Hand: You conjure a magical hand with the same size and strength as your own within Far range.\nMysterious Mist: Make a Spellcast Roll (13) to cast a temporary thick fog that gathers in a stationary area within Very Close range. The fog heavily obscures this area and everything in it.",
  },
  // Dread
  {
    name: "Blighting Strike",
    domain: "Dread",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success: On a roll with Hope, deal d6+1 magic damage using your Proficiency. On a roll with Fear, deal d10+1 magic damage using your Proficiency. The target's next successful attack deals half damage. On a failure, you must spend a Hope or mark a Stress.",
  },
  {
    name: "Umbral Veil",
    domain: "Dread",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Once per rest, you can mark a Stress to encase yourself in shadowy energy. When you do, place a number of tokens on this card equal to the number of Fear in the GM's pool. After an attack roll is made against you, you can spend any number of tokens to give the result a -1 penalty per token spent. At the end of the scene, clear all unspent tokens.",
  },
  {
    name: "Voice of Dread",
    domain: "Dread",
    level: 1,
    type: "Spell",
    recallCost: 0,
    text: "You can magically speak to a creature you can see, tormenting them with your words. Make a Spellcast Roll against them. On a success, they must mark a Stress and are frozen with terror, making them temporarily Restrained.",
  },
  // Grace
  {
    name: "Deft Deceiver",
    domain: "Grace",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "Spend a Hope to gain advantage on a roll to deceive or trick someone into believing a lie you tell them.",
  },
  {
    name: "Enrapture",
    domain: "Grace",
    level: 1,
    type: "Spell",
    recallCost: 0,
    text: "Make a Spellcast Roll against a target within Close range. On a success, they become temporarily Enraptured. While Enraptured, a target's attention is fixed on you, narrowing their field of view and drowning out any sound but your voice. Once per rest on a success, you can mark a Stress to force the Enraptured target to mark a Stress as well.",
  },
  {
    name: "Inspirational Words",
    domain: "Grace",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "Your speech is imbued with power. After a long rest, place a number of tokens on this card equal to your Presence. When you speak with an ally, you can spend a token from this card to give them one benefit from the following options: Your ally clears a Stress, clears a Hit Point, or gains a Hope. When you take a long rest, clear all unspent tokens.",
  },
  // Midnight
  {
    name: "Pick and Pull",
    domain: "Midnight",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "You have advantage on action rolls to pick nonmagical locks, disarm nonmagical traps, or steal items from a target (either through stealth or by force).",
  },
  {
    name: "Rain of Blades",
    domain: "Midnight",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Spend a Hope to make a Spellcast Roll and conjure throwing blades that strike out at all targets within Very Close range. Targets you succeed against take d8+2 magic damage using your Proficiency. If a target you hit is Vulnerable, they take an extra 1d8 damage.",
  },
  {
    name: "Uncanny Disguise",
    domain: "Midnight",
    level: 1,
    type: "Spell",
    recallCost: 0,
    text: "When you have a few minutes to prepare, you can mark a Stress to don the facade of any humanoid you can picture clearly in your mind. While disguised, you have advantage on Presence Rolls to avoid scrutiny. Place a number of tokens equal to your Spellcast trait on this card. When you take an action while disguised, spend a token from this card. After the action that spends the last token is resolved, the disguise drops.",
  },
  // Sage
  {
    name: "Gifted Tracker",
    domain: "Sage",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "When you're tracking a specific creature or group of creatures based on signs of their passage, you can spend any number of Hope and ask the GM that many questions from the tracking list. When you encounter creatures you've tracked in this way, gain a +1 bonus to your Evasion against them.",
  },
  {
    name: "Nature's Tongue",
    domain: "Sage",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "You can speak the language of the natural world. When you want to speak to the plants and animals around you, make an Instinct Roll (12). On a success, they'll give you the information they know. On a roll with Fear, their knowledge might be limited or come at a cost. Additionally, before you make a Spellcast Roll while within a natural environment, you can spend a Hope to gain a +2 bonus to the roll.",
  },
  {
    name: "Vicious Entangle",
    domain: "Sage",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success, roots and vines reach out from the ground, dealing 1d8+1 physical damage and temporarily Restraining the target. Additionally on a success, you can spend a Hope to temporarily Restrain another adversary within Very Close range of your target.",
  },
  // Splendor
  {
    name: "Bolt Beacon",
    domain: "Splendor",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success, spend a Hope to send a bolt of shimmering light toward them, dealing d8+2 magic damage using your Proficiency. The target becomes temporarily Vulnerable and glows brightly until this condition is cleared.",
  },
  {
    name: "Mending Touch",
    domain: "Splendor",
    level: 1,
    type: "Spell",
    recallCost: 1,
    text: "You lay your hands upon a creature and channel healing magic to close their wounds. When you can take a few minutes to focus on the target you're helping, you can spend 2 Hope to clear a Hit Point or a Stress on them. Once per long rest, when you spend this healing time learning something new about them or revealing something about yourself, you can clear 2 Hit Points or 2 Stress on them instead.",
  },
  {
    name: "Reassurance",
    domain: "Splendor",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "Once per rest, after an ally attempts an action roll but before the consequences take place, you can offer assistance or words of support. When you do, your ally can reroll their dice.",
  },
  // Valor
  {
    name: "Bare Bones",
    domain: "Valor",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "When you choose not to equip armor, you have a base Armor Score of 3 + your Strength and use the following as your base damage thresholds: Tier 1: 9/19; Tier 2: 11/24; Tier 3: 13/31; Tier 4: 15/38.",
  },
  {
    name: "Forceful Push",
    domain: "Valor",
    level: 1,
    type: "Ability",
    recallCost: 0,
    text: "Make an attack with your primary weapon against a target within Melee range. On a success, you deal damage and knock them back to Close range. On a success with Hope, add a d6 to your damage roll. Additionally, you can spend a Hope to make them temporarily Vulnerable.",
  },
  {
    name: "I Am Your Shield",
    domain: "Valor",
    level: 1,
    type: "Ability",
    recallCost: 1,
    text: "When an ally within Very Close range would take damage, you can mark a Stress to stand in the way and make yourself the target of the attack instead. When you take damage from this attack, you can mark any number of Armor Slots.",
  },
];

export interface SrdSubclass {
  name: string;
  spellcastTrait?: TraitName;
  foundation: {
    name: string;
    text: string;
  };
}

export interface SrdClass {
  name: string;
  domains: [DomainName, DomainName];
  startingEvasion: number;
  startingHp: number;
  classItems: string;
  hopeFeature: {
    name: string;
    cost: 3;
    text: string;
  };
  classFeatures: Array<{
    name: string;
    text: string;
  }>;
  subclasses: [SrdSubclass, SrdSubclass];
}

export const CLASSES: Record<string, SrdClass> = {
  Assassin: {
    name: "Assassin",
    domains: ["Blade", "Midnight"],
    startingEvasion: 12,
    startingHp: 5,
    classItems: "A list of names with several marked off or a rusted blade inscribed with an insignia",
    hopeFeature: {
      name: "Deadly Determination",
      cost: 3,
      text: "Spend 3 Hope to clear 2 Stress.",
    },
    classFeatures: [
      {
        name: "Marked for Death",
        text: "On a successful weapon attack, mark a Stress to make the target Marked for Death. When you deal damage to a target you've Marked for Death, add a number of d4s equal to your tier to the damage roll. You can have only one adversary Marked for Death at a time.",
      },
      {
        name: "Get In & Get Out",
        text: "Spend a Hope to ask the GM for a quick or inconspicuous way into or out of a place you can see. The next roll you make that acts on this information has advantage.",
      },
    ],
    subclasses: [
      {
        name: "Executioners Guild",
        spellcastTrait: "Agility",
        foundation: {
          name: "First Strike & Ambush",
          text: "First Strike: The first time in a scene you succeed on an attack, you deal double damage. Ambush: Your Marked for Death feature uses d6s instead of d4s.",
        },
      },
      {
        name: "Poisoners Guild",
        spellcastTrait: "Knowledge",
        foundation: {
          name: "Toxic Concoctions",
          text: "Mark a Stress to place 1d4+1 tokens on this card. When you make a successful weapon attack, you can spend a token to afflict the target with a poison: Ghost Petal (Vulnerable), Grave Spore (mark a Stress), or Leech Weed (+1d6 damage).",
        },
      },
    ],
  },
  Bard: {
    name: "Bard",
    domains: ["Codex", "Grace"],
    startingEvasion: 10,
    startingHp: 5,
    classItems: "A romance novel or a letter never opened",
    hopeFeature: {
      name: "Make a Scene",
      cost: 3,
      text: "Spend 3 Hope to temporarily Distract a target within Close range, giving them a -2 penalty to their Difficulty.",
    },
    classFeatures: [
      {
        name: "Rally",
        text: "Once per session, describe how you rally the party and give yourself and each of your allies a Rally Die (d6 at level 1). A PC can spend their Rally Die to roll it, adding the result to their action roll, reaction roll, damage roll, or to clear a number of Stress equal to the result.",
      },
    ],
    subclasses: [
      {
        name: "Troubadour",
        spellcastTrait: "Presence",
        foundation: {
          name: "Gifted Performer",
          text: "You can play three different types of songs, once each per long rest: Relaxing Song (clear a Hit Point on you and all Close allies), Epic Song (Close target temporarily Vulnerable), or Heartbreaking Song (you and all Close allies gain a Hope).",
        },
      },
      {
        name: "Wordsmith",
        spellcastTrait: "Presence",
        foundation: {
          name: "Rousing Speech & Heart of a Poet",
          text: "Rousing Speech: Once per long rest, give an inspiring speech; all allies within Far range clear 2 Stress. Heart of a Poet: After an action roll to impress, persuade, or offend, spend a Hope to add a d4.",
        },
      },
    ],
  },
  Brawler: {
    name: "Brawler",
    domains: ["Valor", "Bone"],
    startingEvasion: 10,
    startingHp: 6,
    classItems: "Hand wraps from a mentor or a book about your secret hobby",
    hopeFeature: {
      name: "Square Up",
      cost: 3,
      text: "Spend 3 Hope to intimidate a target within Close range, making them temporarily Vulnerable.",
    },
    classFeatures: [
      {
        name: "I Am the Weapon",
        text: "Your barehanded attacks are a primary weapon called Brawler's Strike (Melee, d8+d6 phy using Proficiency). While this weapon is active, gain +1 Evasion.",
      },
      {
        name: "Combo Strike",
        text: "After rolling damage on a successful Melee weapon attack, mark a Stress to roll your Combo Die (d4) repeatedly until a roll is lower than the preceding roll, adding all results to damage.",
      },
    ],
    subclasses: [
      {
        name: "Juggernaut",
        foundation: {
          name: "Rugged & Overwhelm",
          text: "Rugged: Permanent +3 bonus to Severe damage threshold. Overwhelm: When you succeed on an attack, spend a Hope to throw the target within Close range or force them to mark a Stress.",
        },
      },
      {
        name: "Martial Artist",
        foundation: {
          name: "Stance Fighter",
          text: "Channel your resolve into martial stances using Focus. Choose two stances from Tier 1 (Favored, Invigorating, Quick, Reliable) to start.",
        },
      },
    ],
  },
  Druid: {
    name: "Druid",
    domains: ["Arcana", "Sage"],
    startingEvasion: 10,
    startingHp: 6,
    classItems: "A small bag of rocks and bones or a strange pendant found in the dirt",
    hopeFeature: {
      name: "Evolution",
      cost: 3,
      text: "Spend 3 Hope to transform into a Beastform without marking a Stress and raise one trait by +1 until you drop out of that Beastform.",
    },
    classFeatures: [
      {
        name: "Beastform",
        text: "Mark a Stress to magically transform into a creature of your tier or lower from the Beastform list, gaining its features, trait bonus, attack, and Evasion bonus.",
      },
      {
        name: "Wildtouch",
        text: "Perform harmless, subtle effects that involve nature at will.",
      },
    ],
    subclasses: [
      {
        name: "Warden of the Elements",
        spellcastTrait: "Instinct",
        foundation: {
          name: "Elemental Incarnation",
          text: "Mark a Stress to Channel Fire (retaliatory 1d10 magic damage), Earth (threshold bonus equal to Proficiency), Water (Close enemies mark Stress), or Air (hover with advantage on Agility Rolls).",
        },
      },
      {
        name: "Warden of Renewal",
        spellcastTrait: "Instinct",
        foundation: {
          name: "Clarity of Nature & Wellspring",
          text: "Clarity of Nature: Once per long rest, create a healing aura; allies clear 2 Hit Points or 2 Stress. Wellspring: When you heal an ally, gain a Hope.",
        },
      },
    ],
  },
  Guardian: {
    name: "Guardian",
    domains: ["Blade", "Valor"],
    startingEvasion: 9,
    startingHp: 7,
    classItems: "A totem from your mentor or a secret key",
    hopeFeature: {
      name: "Frontline Tank",
      cost: 3,
      text: "Spend 3 Hope to clear 2 Armor Slots.",
    },
    classFeatures: [
      {
        name: "Unstoppable",
        text: "Once per long rest, gain an Unstoppable Die (d4) that increments when dealing damage. While Unstoppable, reduce severity of physical damage by one threshold, add die value to damage, and cannot be Restrained or Vulnerable.",
      },
    ],
    subclasses: [
      {
        name: "Stalwart",
        foundation: {
          name: "Unwavering & Iron Will",
          text: "Unwavering: Permanent +1 bonus to damage thresholds. Iron Will: Mark an additional Armor Slot to reduce physical damage severity.",
        },
      },
      {
        name: "Vengeance",
        foundation: {
          name: "At Ease & Revenge",
          text: "At Ease: Gain an additional Stress slot. Revenge: When a Melee adversary attacks you successfully, mark 2 Stress to force the attacker to mark a Hit Point.",
        },
      },
    ],
  },
  Ranger: {
    name: "Ranger",
    domains: ["Bone", "Sage"],
    startingEvasion: 12,
    startingHp: 6,
    classItems: "A trophy from your first kill or a seemingly broken compass",
    hopeFeature: {
      name: "Hold Them Off",
      cost: 3,
      text: "Spend 3 Hope when you succeed on an attack with a weapon to use that same roll against two additional adversaries within range.",
    },
    classFeatures: [
      {
        name: "Ranger's Focus",
        text: "Spend a Hope on an attack to make the target your Focus: know their direction, deal +1 Stress on damage, and end Focus on a miss to reroll Duality Dice.",
      },
    ],
    subclasses: [
      {
        name: "Beastbound",
        spellcastTrait: "Agility",
        foundation: {
          name: "Companion",
          text: "You have an animal companion that stays by your side with its own Companion Sheet, Evasion (10), two Experiences (+2), and d6 damage attack.",
        },
      },
      {
        name: "Wayfinder",
        spellcastTrait: "Agility",
        foundation: {
          name: "Ruthless Predator & Path Forward",
          text: "Ruthless Predator: Mark a Stress on damage to gain +1 Proficiency; when dealing Severe damage, target marks Stress. Path Forward: Identify shortest path to previously visited destination.",
        },
      },
    ],
  },
  Rogue: {
    name: "Rogue",
    domains: ["Grace", "Midnight"],
    startingEvasion: 12,
    startingHp: 6,
    classItems: "A set of forgery tools or a grappling hook",
    hopeFeature: {
      name: "Rogue's Dodge",
      cost: 3,
      text: "Spend 3 Hope to gain a +2 bonus to your Evasion until the next time an attack succeeds against you.",
    },
    classFeatures: [
      {
        name: "Cloaked",
        text: "Any time you would be Hidden, you are instead Cloaked: remain unseen even if stationary when an adversary moves to where they would normally see you.",
      },
      {
        name: "Sneak Attack",
        text: "When you succeed on an attack while Cloaked or while an ally is within Melee range of your target, add d6s equal to your tier to your damage roll.",
      },
    ],
    subclasses: [
      {
        name: "Nightwalker",
        spellcastTrait: "Finesse",
        foundation: {
          name: "Shadow Stepper",
          text: "Mark a Stress to disappear from an area of darkness/shadow and reappear inside another shadow within Far range, becoming Cloaked.",
        },
      },
      {
        name: "Syndicate",
        spellcastTrait: "Finesse",
        foundation: {
          name: "Well-Connected",
          text: "In prominent towns and environments, you know a useful contact with a notable history or favor.",
        },
      },
    ],
  },
  Seraph: {
    name: "Seraph",
    domains: ["Splendor", "Valor"],
    startingEvasion: 9,
    startingHp: 7,
    classItems: "A bundle of offerings or a sigil of your god",
    hopeFeature: {
      name: "Life Support",
      cost: 3,
      text: "Spend 3 Hope to clear a Hit Point on an ally within Close range.",
    },
    classFeatures: [
      {
        name: "Prayer Dice",
        text: "At session start, roll d4s equal to your Spellcast trait as Prayer Dice. Spend them to reduce incoming damage, add to roll results, or gain Hope.",
      },
    ],
    subclasses: [
      {
        name: "Divine Wielder",
        spellcastTrait: "Strength",
        foundation: {
          name: "Spirit Weapon & Sparing Touch",
          text: "Spirit Weapon: Melee/Very Close weapon can fly to attack Close target and return; mark Stress to hit an extra adversary. Sparing Touch: Once per long rest, clear 2 HP or 2 Stress on a creature.",
        },
      },
      {
        name: "Winged Sentinel",
        spellcastTrait: "Strength",
        foundation: {
          name: "Wings of Light",
          text: "You can fly. While flying: mark a Stress to carry another willing creature, or spend a Hope to deal an extra 1d8 damage on a successful attack.",
        },
      },
    ],
  },
  Sorcerer: {
    name: "Sorcerer",
    domains: ["Arcana", "Midnight"],
    startingEvasion: 10,
    startingHp: 6,
    classItems: "A whispering orb or a family heirloom",
    hopeFeature: {
      name: "Volatile Magic",
      cost: 3,
      text: "Spend 3 Hope to reroll any number of your damage dice on an attack that deals magic damage.",
    },
    classFeatures: [
      {
        name: "Arcane Sense",
        text: "Sense the presence of magical people and objects within Close range.",
      },
      {
        name: "Minor Illusion",
        text: "Make a Spellcast Roll (10) to create a minor visual illusion within Close range convincing to anyone at Close range or farther.",
      },
      {
        name: "Channel Raw Power",
        text: "Once per long rest, vault a loadout domain card to either gain Hope equal to its level or add twice its level to a damaging spell's damage roll.",
      },
    ],
    subclasses: [
      {
        name: "Elemental Origin",
        spellcastTrait: "Instinct",
        foundation: {
          name: "Elementalist",
          text: "Choose an element (air, earth, fire, lightning, water) to shape at will. Spend a Hope to gain +2 to an action roll or +3 damage.",
        },
      },
      {
        name: "Primal Origin",
        spellcastTrait: "Instinct",
        foundation: {
          name: "Manipulate Magic",
          text: "After casting a spell or making a magic weapon attack, mark a Stress to extend reach by one range, gain +2 to roll, double a damage die, or hit an additional target.",
        },
      },
    ],
  },
  Warlock: {
    name: "Warlock",
    domains: ["Dread", "Grace"],
    startingEvasion: 11,
    startingHp: 5,
    classItems: "A carving that symbolizes your patron or a ring you can't remove",
    hopeFeature: {
      name: "Patron's Boon",
      cost: 3,
      text: "When you fail a roll, spend 3 Hope to reroll with advantage.",
    },
    classFeatures: [
      {
        name: "Patron's Pact",
        text: "Committed to a supernatural patron with a defined sphere of influence. Spend a Favor to roll your Patron Die (d6) and add it to an action roll related to their sphere.",
      },
      {
        name: "Favor",
        text: "Start with 3 Favor (max 6). Use a downtime move to show tribute and gain Favor equal to Spellcast trait, or take Favor instead of Hope on a roll with Hope.",
      },
    ],
    subclasses: [
      {
        name: "Pact of the Endless",
        spellcastTrait: "Presence",
        foundation: {
          name: "Patron's Mantle & Deathless Embrace",
          text: "Patron's Mantle: Spend a Favor for threshold bonus equal to tier and advantage to intimidate. Deathless Embrace: Spend Favor to roll Patron Dice; each 4+ clears a Hit Point.",
        },
      },
      {
        name: "Pact of the Wrathful",
        spellcastTrait: "Presence",
        foundation: {
          name: "Patron's Fury & Deadly Vengeance",
          text: "Patron's Fury: Spend a Favor to add Patron Dice equal to tier to damage until dealing Severe damage. Deadly Vengeance: Spend Favor when marked HP to roll Patron Dice; each 4+ marks a target HP.",
        },
      },
    ],
  },
  Warrior: {
    name: "Warrior",
    domains: ["Blade", "Bone"],
    startingEvasion: 11,
    startingHp: 6,
    classItems: "The drawing of a lover or a sharpening stone",
    hopeFeature: {
      name: "No Mercy",
      cost: 3,
      text: "Spend 3 Hope to gain a +1 bonus to your attack rolls until your next rest.",
    },
    classFeatures: [
      {
        name: "Attack of Opportunity",
        text: "When an adversary leaves Melee range, make a reaction roll to stop them moving, deal primary weapon damage, or move with them.",
      },
      {
        name: "Combat Training",
        text: "Ignore burden when equipping weapons. Gain bonus to physical damage rolls equal to your level.",
      },
    ],
    subclasses: [
      {
        name: "Call of the Brave",
        foundation: {
          name: "Courage & Battle Ritual",
          text: "Courage: When you fail a roll with Fear, gain a Hope. Battle Ritual: Once per long rest, perform a ritual before facing danger to clear 2 Stress and gain 2 Hope.",
        },
      },
      {
        name: "Call of the Slayer",
        foundation: {
          name: "Slayer",
          text: "Store Slayer Dice (d6) up to Proficiency on rolls with Hope. Spend them to add directly to attack or damage rolls.",
        },
      },
    ],
  },
  Witch: {
    name: "Witch",
    domains: ["Dread", "Sage"],
    startingEvasion: 10,
    startingHp: 6,
    classItems: "A small, harmless pet or a scrying stone",
    hopeFeature: {
      name: "Witch's Charm",
      cost: 3,
      text: "When you or an ally within Far range fails an action roll, spend 3 Hope to change it into a success with Fear.",
    },
    classFeatures: [
      {
        name: "Hex",
        text: "Mark a Stress to Hex a Far target: target takes penalty to damage rolls and Difficulty equal to your tier.",
      },
      {
        name: "Commune",
        text: "Once per long rest, commune with an ancestor, deity, spirit, or otherworldly being to receive sensory or visionary answers.",
      },
    ],
    subclasses: [
      {
        name: "Hedge",
        spellcastTrait: "Knowledge",
        foundation: {
          name: "Herbal Remedies & Enchanted Talisman",
          text: "Herbal Remedies: Consumables clear +1 HP or Stress. Enchanted Talisman: Imbue small item with Hope tokens; holder spends token to reduce HP marked by 1.",
        },
      },
      {
        name: "Moon",
        spellcastTrait: "Instinct",
        foundation: {
          name: "Night's Glamour",
          text: "Make a Spellcast Roll (13) to Glamour yourself; advantage on illusory appearance, and Close adversaries must mark Stress to attack you.",
        },
      },
    ],
  },
  Wizard: {
    name: "Wizard",
    domains: ["Codex", "Splendor"],
    startingEvasion: 11,
    startingHp: 5,
    classItems: "A book you're trying to translate or a tiny, harmless elemental pet",
    hopeFeature: {
      name: "Not This Time",
      cost: 3,
      text: "Spend 3 Hope to force an adversary within Far range to reroll an attack or damage roll.",
    },
    classFeatures: [
      {
        name: "Prestidigitation",
        text: "Perform harmless, subtle magical effects at will.",
      },
      {
        name: "Strange Patterns",
        text: "Choose a number between 1 and 12. When you roll that number on a Duality Die, gain a Hope or clear a Stress.",
      },
    ],
    subclasses: [
      {
        name: "School of Knowledge",
        spellcastTrait: "Knowledge",
        foundation: {
          name: "Prepared & Adept",
          text: "Prepared: Take an additional domain card of your level or lower. Adept: When utilizing an Experience, mark a Stress instead of Hope to double its modifier.",
        },
      },
      {
        name: "School of War",
        spellcastTrait: "Knowledge",
        foundation: {
          name: "Battlemage & Face Your Fear",
          text: "Battlemage: Gain an additional Hit Point slot. Face Your Fear: When you succeed with Fear on an attack roll, deal an extra 1d10 magic damage.",
        },
      },
    ],
  },
};

export interface SrdAncestry {
  name: string;
  description: string;
  feature1: {
    name: string;
    text: string;
  };
  feature2: {
    name: string;
    text: string;
  };
}

export const ANCESTRIES: Record<string, SrdAncestry> = {
  Aetheris: {
    name: "Aetheris",
    description: "Humanoids with wings and sacred glowing markings, descended from angels of the Hallows Above.",
    feature1: {
      name: "Hallowed Aura",
      text: "Once per long rest when an ally within Close range rolls with Fear, you can change it into a roll with Hope instead.",
    },
    feature2: {
      name: "Celestial Wings",
      text: "You have wings that allow you to fly. Once per scene while flying, you can spend a Hope instead of marking an Armor Slot.",
    },
  },
  Clank: {
    name: "Clank",
    description: "Sentient mechanical beings built from metal, wood, and stone with bespoke body configurations.",
    feature1: {
      name: "Purposeful Design",
      text: "Decide who made you and for what purpose. At character creation, choose one of your Experiences that best aligns with this purpose and gain a permanent +1 bonus to it.",
    },
    feature2: {
      name: "Efficient",
      text: "When you take a short rest, you can choose a long rest move instead of a short rest move.",
    },
  },
  Drakona: {
    name: "Drakona",
    description: "Wingless dragon-like humanoids with thick natural scales and powerful elemental breath.",
    feature1: {
      name: "Scales",
      text: "Your scales act as natural protection. When you would take Severe damage, you can mark a Stress to mark 1 fewer Hit Points.",
    },
    feature2: {
      name: "Elemental Breath",
      text: "Choose an element for your breath (such as electricity, fire, or ice). You can use this breath against a target or group within Very Close range, treating it as an Instinct weapon that deals d8 magic damage using your Proficiency.",
    },
  },
  Dwarf: {
    name: "Dwarf",
    description: "Short humanoids with dense musculature, square frames, thick keratin-rich skin and resilient bones.",
    feature1: {
      name: "Thick Skin",
      text: "When you take Minor damage, you can mark 2 Stress instead of marking a Hit Point.",
    },
    feature2: {
      name: "Increased Fortitude",
      text: "Spend 3 Hope to halve incoming physical damage.",
    },
  },
  Earthkin: {
    name: "Earthkin",
    description: "Elemental kin formed of flesh and stone, soil, clay, crystal, or rock.",
    feature1: {
      name: "Stoneskin",
      text: "Gain a permanent +1 bonus to your Armor Score and damage thresholds at character creation.",
    },
    feature2: {
      name: "Immovable",
      text: "While you're touching the ground, you can't be lifted or moved against your will.",
    },
  },
  Elf: {
    name: "Elf",
    description: "Tall humanoids with pointed ears, acutely attuned senses, and the ability to drop into a celestial trance.",
    feature1: {
      name: "Quick Reactions",
      text: "Mark a Stress to gain advantage on a reaction roll.",
    },
    feature2: {
      name: "Celestial Trance",
      text: "During a rest, you can drop into a trance to choose an additional downtime move.",
    },
  },
  Emberkin: {
    name: "Emberkin",
    description: "Elemental kin formed of flesh and flame, with fiery hair and smoldering presence.",
    feature1: {
      name: "Fireproof",
      text: "You are immune to damage from magical or mundane flame.",
    },
    feature2: {
      name: "Ignition",
      text: "Mark a Stress to wreathe your primary weapon in flame until the end of the scene, shedding bright light and gaining a 1d6 bonus to damage rolls.",
    },
  },
  Faerie: {
    name: "Faerie",
    description: "Winged humanoid creatures with insectile features and close bonds to nature.",
    feature1: {
      name: "Luckbender",
      text: "Once per session, after you or a willing ally within Close range makes an action roll, spend 3 Hope to reroll the Duality Dice.",
    },
    feature2: {
      name: "Wings",
      text: "You can fly. While flying, mark a Stress after an adversary attacks you to gain a +2 bonus to your Evasion against that attack.",
    },
  },
  Faun: {
    name: "Faun",
    description: "Caprine humanoids with curving horns, cloven hooves, square pupils, and agile legs.",
    feature1: {
      name: "Caprine Leap",
      text: "You can leap anywhere within Close range as though you were using normal movement to vault obstacles or scale barriers.",
    },
    feature2: {
      name: "Kick",
      text: "When you succeed on an attack within Melee range, mark a Stress to deal an extra 2d6 damage and knock back yourself or the target to Very Close range.",
    },
  },
  Firbolg: {
    name: "Firbolg",
    description: "Bovine humanoids with broad noses, drooping ears, horns, and incredible muscular strength.",
    feature1: {
      name: "Charge",
      text: "When you succeed on an Agility Roll to move from Far/Very Far into Melee range with one or more targets, mark a Stress to deal 1d12 physical damage to all targets within Melee range.",
    },
    feature2: {
      name: "Unshakable",
      text: "When you would mark a Stress, roll a d6. On a result of 6, don't mark it.",
    },
  },
  Fungril: {
    name: "Fungril",
    description: "Humanoid mushroom folk with connected mycelial networks and deep ties to natural decay.",
    feature1: {
      name: "Fungril Network",
      text: "Make an Instinct Roll (12) to use your mycelial array to communicate with others of your ancestry across any distance.",
    },
    feature2: {
      name: "Death Connection",
      text: "While touching a recently deceased corpse, mark a Stress to extract one memory related to an emotion or sensation of your choice.",
    },
  },
  Galapa: {
    name: "Galapa",
    description: "Anthropomorphic turtle humanoids with large domed shells into which they can retract.",
    feature1: {
      name: "Shell",
      text: "Gain a bonus to your damage thresholds equal to your Proficiency.",
    },
    feature2: {
      name: "Retract",
      text: "Mark a Stress to retract into your shell, gaining resistance to physical damage, disadvantage on action rolls, and unable to move.",
    },
  },
  Giant: {
    name: "Giant",
    description: "Towering humanoids with broad shoulders, long reaching limbs, and 1 to 3 eyes.",
    feature1: {
      name: "Endurance",
      text: "Gain an additional Hit Point slot at character creation.",
    },
    feature2: {
      name: "Reach",
      text: "Treat any weapon, ability, spell, or other feature with Melee range as having Very Close range instead.",
    },
  },
  Gnome: {
    name: "Gnome",
    description: "Small humanoids with conical heads, long dexterous hands, and innate teleportation.",
    feature1: {
      name: "Nimble Fingers",
      text: "When you make a Finesse Roll, you can spend 2 Hope to reroll your Hope Die.",
    },
    feature2: {
      name: "Flicker Step",
      text: "Once per scene, you can teleport to another point you can see within Far range.",
    },
  },
  Goblin: {
    name: "Goblin",
    description: "Small humanoids with large membranous ears, sharp eyesight, and keen dark vision.",
    feature1: {
      name: "Surefooted",
      text: "You ignore disadvantage on Agility Rolls.",
    },
    feature2: {
      name: "Danger Sense",
      text: "Once per rest, mark a Stress to force an adversary to reroll an attack against you or an ally within Very Close range.",
    },
  },
  Halfling: {
    name: "Halfling",
    description: "Small humanoids with large hairy feet, rounded ears, and natural internal compasses.",
    feature1: {
      name: "Luckbringer",
      text: "At the start of each session, everyone in your party gains a Hope.",
    },
    feature2: {
      name: "Internal Compass",
      text: "When you roll a 1 on your Hope Die, you can reroll it.",
    },
  },
  Human: {
    name: "Human",
    description: "Enduring, adaptable humanoids with dexterous hands, rounded ears, and high stamina.",
    feature1: {
      name: "High Stamina",
      text: "Gain an additional Stress slot at character creation.",
    },
    feature2: {
      name: "Adaptability",
      text: "When you fail a roll that utilized one of your Experiences, you can mark a Stress to reroll.",
    },
  },
  Infernis: {
    name: "Infernis",
    description: "Horned humanoids with sharp canines and tails, descended from demons of the Circles Below.",
    feature1: {
      name: "Fearless",
      text: "When you roll with Fear, you can mark 2 Stress to change it into a roll with Hope instead.",
    },
    feature2: {
      name: "Dread Visage",
      text: "You have advantage on rolls to intimidate hostile creatures.",
    },
  },
  Katari: {
    name: "Katari",
    description: "Feline humanoids with triangular swiveling ears, slit pupils, fur, and retractable claws.",
    feature1: {
      name: "Feline Instincts",
      text: "When you make an Agility Roll, you can spend 2 Hope to reroll your Hope Die.",
    },
    feature2: {
      name: "Retracting Claws",
      text: "Make an Agility Roll to scratch a target within Melee range. On a success, they become temporarily Vulnerable.",
    },
  },
  Orc: {
    name: "Orc",
    description: "Sturdy humanoids with square features, muscular builds, and lower jaw tusks.",
    feature1: {
      name: "Sturdy",
      text: "When you have 1 Hit Point remaining, attacks against you have disadvantage.",
    },
    feature2: {
      name: "Tusks",
      text: "When you succeed on an attack against a target within Melee range, spend a Hope to gore the target with your tusks for an extra 1d6 damage.",
    },
  },
  Ribbet: {
    name: "Ribbet",
    description: "Amphibious frog folk with webbed digits, smooth skin, hopping locomotion, and long prehensile tongues.",
    feature1: {
      name: "Amphibious",
      text: "You can breathe and move naturally underwater.",
    },
    feature2: {
      name: "Long Tongue",
      text: "Use your tongue within Close range. Mark a Stress to treat it as a Finesse Close weapon dealing d12 physical damage using Proficiency.",
    },
  },
  Simiah: {
    name: "Simiah",
    description: "Anthropomorphic monkey and ape folk with prehensile feet and extraordinary climbing agility.",
    feature1: {
      name: "Natural Climber",
      text: "You have advantage on Agility Rolls that involve balancing and climbing.",
    },
    feature2: {
      name: "Nimble",
      text: "Gain a permanent +1 bonus to your Evasion at character creation.",
    },
  },
  Skykin: {
    name: "Skykin",
    description: "Elemental kin formed of flesh and air, with color-shifting skytone skin and floating hair.",
    feature1: {
      name: "Gale Force",
      text: "Mark a Stress to conjure a gust of wind carrying you or a Very Close ally up to Very Far range. You can always control the speed at which you fall.",
    },
    feature2: {
      name: "Eye of the Storm",
      text: "Spend 2 Hope to grant yourself or an ally within Melee range a +1 bonus to Evasion until taking Severe damage.",
    },
  },
  Tidekin: {
    name: "Tidekin",
    description: "Elemental kin formed of flesh and water, with finlike ears and aqueous fluid markings.",
    feature1: {
      name: "Amphibious",
      text: "You can breathe and move naturally underwater.",
    },
    feature2: {
      name: "Lifespring",
      text: "Once per rest when you have access to a small amount of water, mark a Stress to clear a Hit Point on yourself or an ally within Very Close range.",
    },
  },
};

export interface MixedAncestryHeritage {
  name: string;
  ancestryTop: string;
  ancestryBottom: string;
  feature1: {
    name: string;
    text: string;
  };
  feature2: {
    name: string;
    text: string;
  };
}

export function createMixedAncestry(
  ancestryTopName: string,
  ancestryBottomName: string,
  customName?: string,
): MixedAncestryHeritage {
  const top = ANCESTRIES[ancestryTopName];
  const bottom = ANCESTRIES[ancestryBottomName];
  if (!top) throw new Error(`Unknown top ancestry: ${ancestryTopName}`);
  if (!bottom) throw new Error(`Unknown bottom ancestry: ${ancestryBottomName}`);

  return {
    name: customName || `${top.name}-${bottom.name}`,
    ancestryTop: top.name,
    ancestryBottom: bottom.name,
    feature1: { ...top.feature1 },
    feature2: { ...bottom.feature2 },
  };
}

export interface SrdCommunity {
  name: string;
  description: string;
  adjectives: string[];
  feature: {
    name: string;
    text: string;
  };
}

export const COMMUNITIES: Record<string, SrdCommunity> = {
  Duneborne: {
    name: "Duneborne",
    description: "Communities built among the shifting sands and arid climate of the desert.",
    adjectives: ["adaptable", "centered", "collaborative", "devoted", "cheerful", "observant"],
    feature: {
      name: "Oasis",
      text: "During a short rest, you or an ally can reroll a die used for a downtime move and take the higher result.",
    },
  },
  Freeborne: {
    name: "Freeborne",
    description: "Collectives that once lived under tyrannical rule but are now liberated.",
    adjectives: ["optimistic", "guarded", "courageous", "joyful", "subversive", "traumatized"],
    feature: {
      name: "Unbound",
      text: "Once per session when you roll with Fear, you can change it into a roll with Hope instead.",
    },
  },
  Frostborne: {
    name: "Frostborne",
    description: "Communities forged in the bitter and unrelenting cold of glaciers and snowbound tundras.",
    adjectives: ["cunning", "gregarious", "intrepid", "purposeful", "quiet", "warm"],
    feature: {
      name: "Hardy",
      text: "When you take a rest, you clear a Hit Point.",
    },
  },
  Hearthborne: {
    name: "Hearthborne",
    description: "Humble, close-knit agrarian villages and quaint countryside hamlets.",
    adjectives: ["careful", "generous", "insular", "loquacious", "smiley", "traditional"],
    feature: {
      name: "Close-Knit",
      text: "Once per long rest, you can spend any number of Hope to grant an ally within Far range an equal number of Hope.",
    },
  },
  Highborne: {
    name: "Highborne",
    description: "Accustomed to lives of opulence, prestige, titles, and influence in upper-echelon society.",
    adjectives: ["amiable", "candid", "conniving", "enterprising", "ostentatious", "unflappable"],
    feature: {
      name: "Privilege",
      text: "You have advantage on rolls to consort with nobles, negotiate prices, or leverage your reputation to get what you want.",
    },
  },
  Loreborne: {
    name: "Loreborne",
    description: "Societies that favor strong academic or political prowess, historical preservation, and scientific study.",
    adjectives: ["direct", "eloquent", "inquisitive", "patient", "rhapsodic", "witty"],
    feature: {
      name: "Well-Read",
      text: "You have advantage on rolls that involve the history, culture, or politics of a prominent person or place.",
    },
  },
  Orderborne: {
    name: "Orderborne",
    description: "Collectives focusing on discipline, faith, common doctrine, or structured military prowess.",
    adjectives: ["ambitious", "benevolent", "pensive", "prudent", "sardonic", "stoic"],
    feature: {
      name: "Dedicated",
      text: "Record three sayings or values your upbringing instilled in you. Once per rest, when you describe how you're embodying one of these principles through your current action, roll a d20 as your Hope Die.",
    },
  },
  Reborne: {
    name: "Reborne",
    description: "Individuals separated from or returning from outside a former community, unified by solitary journeys.",
    adjectives: ["curious", "hopeful", "innocent", "isolated", "mournful", "uncanny"],
    feature: {
      name: "Found Family",
      text: "Once per rest, spend a Hope to use an ally's Experience as if it were your own.",
    },
  },
  Ridgeborne: {
    name: "Ridgeborne",
    description: "Thriving upon rocky mountain peaks, high cliffs, and rugged high-ground strongholds.",
    adjectives: ["bold", "hardy", "indomitable", "loyal", "reserved", "stubborn"],
    feature: {
      name: "Steady",
      text: "You have advantage on rolls to traverse dangerous cliffs and ledges, navigate harsh environments, and use your survival knowledge.",
    },
  },
  Seaborne: {
    name: "Seaborne",
    description: "Communities built upon harbor shorelines, archipelago ports, or ships on the open seas.",
    adjectives: ["candid", "cooperative", "exuberant", "fierce", "resolute", "weathered"],
    feature: {
      name: "Know the Tide",
      text: "When you roll with Fear, place a token on your community card (up to your level). Before an action roll, spend any tokens for +1 to the roll per token spent.",
    },
  },
  Slyborne: {
    name: "Slyborne",
    description: "Underground collectives operating outside the law: grifters, smugglers, scoundrels, and thieves.",
    adjectives: ["calculating", "clever", "formidable", "perceptive", "shrewd", "tenacious"],
    feature: {
      name: "Scoundrel",
      text: "You have advantage on rolls to negotiate with criminals, detect lies, or find a safe place to hide.",
    },
  },
  Underborne: {
    name: "Underborne",
    description: "Subterranean enclaves and cavern metropolises living beneath the surface.",
    adjectives: ["composed", "elusive", "indomitable", "innovative", "resourceful", "unpretentious"],
    feature: {
      name: "Low-Light Living",
      text: "In areas with low light or heavy shadow, you have advantage on rolls to hide, investigate, or perceive details.",
    },
  },
  Wanderborne: {
    name: "Wanderborne",
    description: "Nomadic travelers forgoing permanent homes, collecting knowledge, skills, and deep party bonds.",
    adjectives: ["inscrutable", "magnanimous", "mirthful", "reliable", "savvy", "unorthodox"],
    feature: {
      name: "Nomadic Pack",
      text: "Add a Nomadic Pack to your inventory. Once per session, spend a Hope to pull out a mundane item that's useful to your situation.",
    },
  },
  Warborne: {
    name: "Warborne",
    description: "Communities ravaged by or forged inside active warzones, military sieges, and conflict.",
    adjectives: ["strategic", "devastated", "determined", "calculating", "unwavering", "devoted"],
    feature: {
      name: "Brave Face",
      text: "Once per session when you would be forced to mark a Stress, you can spend a Hope instead.",
    },
  },
  Wildborne: {
    name: "Wildborne",
    description: "Reclusive forest enclaves dedicated to harmony and conservation of untamed wilderness.",
    adjectives: ["hardy", "loyal", "nurturing", "reclusive", "sagacious", "vibrant"],
    feature: {
      name: "Lightfoot",
      text: "Your movement is naturally silent. You have advantage on rolls to move without being heard.",
    },
  },
};

export type WeaponRange = "Melee" | "Very Close" | "Close" | "Far" | "Very Far";
export type DamageType = "phy" | "mag" | "phy/mag";
export type WeaponBurden = "One-Handed" | "Two-Handed";

export interface SrdWeapon {
  name: string;
  tier: 1;
  category: "primary" | "secondary";
  type: "physical" | "magic";
  trait: TraitName | "Spellcast";
  range: WeaponRange;
  damage: string;
  damageType: DamageType;
  burden: WeaponBurden;
  feature?: string;
}

export const TIER_1_PRIMARY_WEAPONS: SrdWeapon[] = [
  // Physical
  { name: "Broadsword", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Reliable: +1 to attack rolls" },
  { name: "Katana", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Melee", damage: "d10+3", damageType: "phy", burden: "Two-Handed", feature: "Quick: When you make an attack, you can mark a Stress to target another creature within range." },
  { name: "Longsword", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Melee", damage: "d10+3", damageType: "phy", burden: "Two-Handed" },
  { name: "Battleaxe", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d10+3", damageType: "phy", burden: "Two-Handed" },
  { name: "Brass Knuckles", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d8+1", damageType: "phy", burden: "One-Handed" },
  { name: "Greatsword", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d10+3", damageType: "phy", burden: "Two-Handed", feature: "Massive: -1 to Evasion; on a successful attack, roll an additional damage die and discard the lowest result." },
  { name: "Mace", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d8+1", damageType: "phy", burden: "One-Handed" },
  { name: "Warhammer", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d12+3", damageType: "phy", burden: "Two-Handed", feature: "Heavy: -1 to Evasion" },
  { name: "Dagger", tier: 1, category: "primary", type: "physical", trait: "Finesse", range: "Melee", damage: "d8+1", damageType: "phy", burden: "One-Handed" },
  { name: "Quarterstaff", tier: 1, category: "primary", type: "physical", trait: "Instinct", range: "Melee", damage: "d10+3", damageType: "phy", burden: "Two-Handed" },
  { name: "Cutlass", tier: 1, category: "primary", type: "physical", trait: "Presence", range: "Melee", damage: "d8+1", damageType: "phy", burden: "One-Handed" },
  { name: "Rapier", tier: 1, category: "primary", type: "physical", trait: "Presence", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Quick: When you make an attack, you can mark a Stress to target another creature within range." },
  { name: "Scimitar", tier: 1, category: "primary", type: "physical", trait: "Presence", range: "Melee", damage: "d8+1", damageType: "phy", burden: "One-Handed" },
  { name: "Twisted Dagger", tier: 1, category: "primary", type: "physical", trait: "Knowledge", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Piercing: Damage dealt treats target's Major threshold as having a -2 penalty." },
  { name: "Halberd", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Very Close", damage: "d10+2", damageType: "phy", burden: "Two-Handed", feature: "Cumbersome: -1 to Finesse" },
  { name: "Spear", tier: 1, category: "primary", type: "physical", trait: "Finesse", range: "Very Close", damage: "d8+3", damageType: "phy", burden: "Two-Handed" },
  { name: "Whipsword", tier: 1, category: "primary", type: "physical", trait: "Finesse", range: "Very Close", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Versatile: Can also be used as Finesse, Melee, d10." },
  { name: "Rope Dart", tier: 1, category: "primary", type: "physical", trait: "Instinct", range: "Close", damage: "d6+1", damageType: "phy", burden: "Two-Handed" },
  { name: "Shortbow", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Far", damage: "d6+3", damageType: "phy", burden: "Two-Handed" },
  { name: "Crossbow", tier: 1, category: "primary", type: "physical", trait: "Finesse", range: "Far", damage: "d6+1", damageType: "phy", burden: "One-Handed" },
  { name: "Longbow", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Very Far", damage: "d8+3", damageType: "phy", burden: "Two-Handed", feature: "Cumbersome: -1 to Finesse" },
  // Wheelchair models
  { name: "Light-Frame Wheelchair", tier: 1, category: "primary", type: "physical", trait: "Agility", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Quick: When you make an attack, you can mark a Stress to target another creature within range." },
  { name: "Heavy-Frame Wheelchair", tier: 1, category: "primary", type: "physical", trait: "Strength", range: "Melee", damage: "d12+3", damageType: "phy", burden: "Two-Handed", feature: "Heavy: -1 to Evasion" },
  // Magic
  { name: "Arcane Gauntlets", tier: 1, category: "primary", type: "magic", trait: "Strength", range: "Melee", damage: "d10+3", damageType: "mag", burden: "Two-Handed" },
  { name: "Brightsword", tier: 1, category: "primary", type: "magic", trait: "Strength", range: "Melee", damage: "d10+3", damageType: "mag", burden: "Two-Handed" },
  { name: "Hallowed Axe", tier: 1, category: "primary", type: "magic", trait: "Strength", range: "Melee", damage: "d8+1", damageType: "mag", burden: "One-Handed" },
  { name: "Shadowblade", tier: 1, category: "primary", type: "magic", trait: "Presence", range: "Melee", damage: "d8", damageType: "phy/mag", burden: "One-Handed", feature: "Otherworldly: On a successful attack, you can deal physical or magic damage." },
  { name: "Glowing Rings", tier: 1, category: "primary", type: "magic", trait: "Agility", range: "Very Close", damage: "d10+2", damageType: "mag", burden: "Two-Handed" },
  { name: "Hand Runes", tier: 1, category: "primary", type: "magic", trait: "Instinct", range: "Very Close", damage: "d10", damageType: "mag", burden: "One-Handed" },
  { name: "Enchanted Chakram", tier: 1, category: "primary", type: "magic", trait: "Finesse", range: "Close", damage: "d6+1", damageType: "mag", burden: "One-Handed", feature: "Ricochet: Returns to hand; mark a Stress to target an additional creature within Very Close range of the first." },
  { name: "Returning Blade", tier: 1, category: "primary", type: "magic", trait: "Finesse", range: "Close", damage: "d8", damageType: "mag", burden: "One-Handed", feature: "Returning: Appears in your hand immediately after the attack." },
  { name: "Casting Dagger", tier: 1, category: "primary", type: "magic", trait: "Instinct", range: "Close", damage: "d6", damageType: "mag", burden: "One-Handed", feature: "Versatile: Can also be used as Instinct, Melee, d8." },
  { name: "Shortstaff", tier: 1, category: "primary", type: "magic", trait: "Instinct", range: "Close", damage: "d8+1", damageType: "mag", burden: "One-Handed" },
  { name: "Dualstaff", tier: 1, category: "primary", type: "magic", trait: "Instinct", range: "Far", damage: "d6+3", damageType: "mag", burden: "Two-Handed" },
  { name: "Scepter", tier: 1, category: "primary", type: "magic", trait: "Presence", range: "Far", damage: "d6", damageType: "mag", burden: "Two-Handed", feature: "Versatile: Can also be used as Presence, Melee, d8." },
  { name: "Runelock Pistol", tier: 1, category: "primary", type: "magic", trait: "Knowledge", range: "Far", damage: "d6+3", damageType: "mag", burden: "One-Handed", feature: "Reloading: On a d6 roll of 1 after attacking, mark a Stress to reload." },
  { name: "Wand", tier: 1, category: "primary", type: "magic", trait: "Knowledge", range: "Far", damage: "d6+1", damageType: "mag", burden: "One-Handed" },
  { name: "Arcane Rifle", tier: 1, category: "primary", type: "magic", trait: "Agility", range: "Very Far", damage: "d10+3", damageType: "mag", burden: "Two-Handed", feature: "Aimed: Disadvantage if target is within Very Close or Melee of ally; mark a Stress to ignore." },
  { name: "Greatstaff", tier: 1, category: "primary", type: "magic", trait: "Knowledge", range: "Very Far", damage: "d6", damageType: "mag", burden: "Two-Handed", feature: "Powerful: Roll an additional damage die and discard lowest." },
  { name: "Arcane-Frame Wheelchair", tier: 1, category: "primary", type: "magic", trait: "Spellcast", range: "Far", damage: "d6", damageType: "mag", burden: "One-Handed", feature: "Reliable: +1 to attack rolls" },
];

export const TIER_1_SECONDARY_WEAPONS: SrdWeapon[] = [
  { name: "Hatchet", tier: 1, category: "secondary", type: "physical", trait: "Agility", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Follow-Up: On successful primary attack in Melee, mark a Stress for +1 Proficiency." },
  { name: "Shortsword", tier: 1, category: "secondary", type: "physical", trait: "Agility", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Paired: +2 to primary weapon damage within Melee range." },
  { name: "Offhand Brass Knuckles", tier: 1, category: "secondary", type: "physical", trait: "Strength", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Paired: +2 to primary weapon damage within Melee range." },
  { name: "Round Shield", tier: 1, category: "secondary", type: "physical", trait: "Strength", range: "Melee", damage: "d4", damageType: "phy", burden: "One-Handed", feature: "Protective: +1 to Armor Score." },
  { name: "Tower Shield", tier: 1, category: "secondary", type: "physical", trait: "Strength", range: "Melee", damage: "d6", damageType: "phy", burden: "One-Handed", feature: "Barrier: +2 to Armor Score; -1 to Evasion." },
  { name: "Small Dagger", tier: 1, category: "secondary", type: "physical", trait: "Finesse", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Paired: +2 to primary weapon damage within Melee range." },
  { name: "Throwing Knives", tier: 1, category: "secondary", type: "physical", trait: "Finesse", range: "Melee", damage: "d8", damageType: "phy", burden: "One-Handed", feature: "Stockpiled: Can throw within Close range using Finesse; always have another on hand." },
  { name: "Fighting Cloak", tier: 1, category: "secondary", type: "physical", trait: "Presence", range: "Melee", damage: "d4", damageType: "phy", burden: "One-Handed", feature: "Padded: +2 to damage thresholds." },
  { name: "Rune Shield", tier: 1, category: "secondary", type: "magic", trait: "Knowledge", range: "Melee", damage: "d4", damageType: "mag", burden: "One-Handed", feature: "Protective: +1 to Armor Score." },
  { name: "Focus Runes", tier: 1, category: "secondary", type: "magic", trait: "Instinct", range: "Very Close", damage: "d6", damageType: "mag", burden: "One-Handed", feature: "Focused: +1 to primary weapon damage within Very Close range." },
  { name: "Whip", tier: 1, category: "secondary", type: "physical", trait: "Presence", range: "Very Close", damage: "d6", damageType: "phy", burden: "One-Handed", feature: "Startling: Mark a Stress to force all Melee adversaries back to Close range." },
  { name: "Grappler", tier: 1, category: "secondary", type: "physical", trait: "Finesse", range: "Close", damage: "d6", damageType: "phy", burden: "One-Handed", feature: "Hooked: On successful attack, pull target into Melee range." },
  { name: "Hand Crossbow", tier: 1, category: "secondary", type: "physical", trait: "Finesse", range: "Far", damage: "d6+1", damageType: "phy", burden: "One-Handed" },
];

export interface SrdArmor {
  name: string;
  tier: 1;
  baseMajor: number;
  baseSevere: number;
  baseScore: number;
  feature?: string;
}

export const TIER_1_ARMOR: SrdArmor[] = [
  { name: "Mage Robes", tier: 1, baseMajor: 4, baseSevere: 10, baseScore: 2, feature: "Enchanted: Gain a bonus to your damage thresholds equal to your Spellcast trait." },
  { name: "Gambeson Armor", tier: 1, baseMajor: 5, baseSevere: 11, baseScore: 3, feature: "Flexible: +1 to Evasion" },
  { name: "Brigandine Armor", tier: 1, baseMajor: 6, baseSevere: 12, baseScore: 3, feature: "Lined: Mark a Stress to negate Minor damage." },
  { name: "Leather Armor", tier: 1, baseMajor: 6, baseSevere: 13, baseScore: 3 },
  { name: "Scale Mail Armor", tier: 1, baseMajor: 7, baseSevere: 14, baseScore: 3, feature: "Cumbersome: -1 to Finesse" },
  { name: "Chainmail Armor", tier: 1, baseMajor: 7, baseSevere: 15, baseScore: 4, feature: "Heavy: -1 to Evasion" },
  { name: "Banded Armor", tier: 1, baseMajor: 8, baseSevere: 16, baseScore: 4, feature: "Bulky: -1 to Evasion; when you take Severe damage, you must mark a Stress." },
  { name: "Full Plate Armor", tier: 1, baseMajor: 8, baseSevere: 17, baseScore: 4, feature: "Very Heavy: -2 to Evasion; -1 to Agility" },
];
